import { z } from "zod";

/**
 * CauseTide's own subscription (what an org pays CauseTide), priced by the size
 * of the donor file — every feature on every plan, unlimited users. Mirrors the
 * public price list on causetide.com. Amounts are integer cents.
 */
export const BILLING_PLANS = [
  { key: "free", maxRecords: 1_000, monthly: 0, annual: 0 },
  { key: "r2500", maxRecords: 2_500, monthly: 4_900, annual: 49_000 },
  { key: "r5000", maxRecords: 5_000, monthly: 8_900, annual: 89_000 },
  { key: "r10000", maxRecords: 10_000, monthly: 14_900, annual: 149_000 },
  { key: "r25000", maxRecords: 25_000, monthly: 27_900, annual: 279_000 },
  { key: "r50000", maxRecords: 50_000, monthly: 44_900, annual: 449_000 },
  { key: "r100000", maxRecords: 100_000, monthly: 69_900, annual: 699_000 },
  { key: "r250000", maxRecords: 250_000, monthly: 129_900, annual: 1_299_000 },
] as const;

export type BillingPlanKey = (typeof BILLING_PLANS)[number]["key"];
export type PaidPlanKey = Exclude<BillingPlanKey, "free">;
export type BillingInterval = "month" | "year";
export const PAID_PLAN_KEYS = BILLING_PLANS.filter((p) => p.key !== "free").map((p) => p.key) as PaidPlanKey[];

export const billingPlan = (key: string) => BILLING_PLANS.find((p) => p.key === key) ?? null;

/** the smallest plan that covers this many records (null = over 250k: talk to us) */
export function planForRecords(records: number): (typeof BILLING_PLANS)[number] | null {
  return BILLING_PLANS.find((p) => records <= p.maxRecords) ?? null;
}

/** Stripe price lookup key, e.g. causetide_r2500_month — created by `pnpm stripe:setup` */
export const billingLookupKey = (plan: PaidPlanKey, interval: BillingInterval) => `causetide_${plan}_${interval}`;
export function parseBillingLookupKey(key: string | null | undefined): { plan: PaidPlanKey; interval: BillingInterval } | null {
  const m = /^causetide_(r\d+)_(month|year)$/.exec(key ?? "");
  if (!m || !(PAID_PLAN_KEYS as string[]).includes(m[1]!)) return null;
  return { plan: m[1] as PaidPlanKey, interval: m[2] as BillingInterval };
}

export const zBillingCheckout = z.object({
  plan: z.enum(PAID_PLAN_KEYS as [PaidPlanKey, ...PaidPlanKey[]]),
  interval: z.enum(["month", "year"]),
});
export type BillingCheckout = z.infer<typeof zBillingCheckout>;

export interface BillingStatus {
  /** STRIPE_SECRET_KEY is set (otherwise every org is on Free and nothing can be bought) */
  configured: boolean;
  /** sandboxes are never billed */
  sandbox: boolean;
  plan: BillingPlanKey;
  interval: BillingInterval | null;
  /** Stripe subscription status (active, trialing, past_due, canceled, …) or null on Free */
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** has a Stripe customer — the billing portal (invoices, card, plan changes, cancel) is available */
  hasBillingAccount: boolean;
  /** live donor records (people, households, organizations; merged and deleted excluded) */
  records: number;
  planMaxRecords: number;
  /** soft limit: never blocks data entry, only prompts an upgrade */
  overLimit: boolean;
  /** smallest plan covering the current record count (null = over 250k) */
  suggestedPlan: BillingPlanKey | null;
}
